import type {
  TimelineAnnotation,
  TimelineAnnotationKind,
  TimelineMarker,
} from "@/state/timeline/timeline.types";

export const TIMELINE_PUBLISH_STRIP_CONTRACT_VERSION = 1 as const;

export type TimelinePublishStripSignalKind = "npc_runtime" | "surface_intent" | "phase" | "import_context";

export type TimelinePublishStripLaneKind = TimelineAnnotationKind | "runtime";

export type TimelinePublishStripLaneState = "present" | "suggested" | "missing" | "suppressed";

export interface TimelinePublishStripSignal {
  kind: TimelinePublishStripSignalKind;
  source: "timeline" | "runtime" | "import" | "system";
  label: string;
  intensity01?: number | null;
  thresholdsCrossed?: string[];
  payload?: Record<string, unknown> | null;
}

export interface TimelinePublishStripLaneEntry {
  lane: TimelinePublishStripLaneKind;
  state: TimelinePublishStripLaneState;
  summary: string | null;
  nextAction: string | null;
  annotationIds: string[];
  markerIds: string[];
  signals: TimelinePublishStripSignal[];
}

export interface TimelinePublishStripSlice {
  id: string;
  startSec: number;
  endSec: number;
  anchorMarkerId?: string | null;
  lanes: TimelinePublishStripLaneEntry[];
  completeness: {
    requiredLaneCount: number;
    satisfiedLaneCount: number;
    missingLanes: TimelinePublishStripLaneKind[];
  };
}

export interface TimelinePublishStripImportContext {
  importedKind: "video" | "audio" | "image" | "npc";
  clipId: string;
  trackId: string;
  sourceLabel?: string | null;
}

export interface TimelinePublishStripCompileInput {
  durationSec: number;
  fps?: number;
  annotations: TimelineAnnotation[];
  markers: TimelineMarker[];
  signals?: TimelinePublishStripSignal[];
  importContext?: TimelinePublishStripImportContext | null;
}

export interface TimelinePublishStripContractV1 {
  v: typeof TIMELINE_PUBLISH_STRIP_CONTRACT_VERSION;
  slices: TimelinePublishStripSlice[];
  sourceAnnotationCount: number;
  sourceMarkerCount: number;
}

function clampFinite(value: unknown, fallback = 0): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function annotationTimeSec(annotation: TimelineAnnotation, markerByAnnotationId: Map<string, TimelineMarker>) {
  if (typeof annotation.timeSec === "number" && Number.isFinite(annotation.timeSec)) return annotation.timeSec;
  const marker = markerByAnnotationId.get(String(annotation.id ?? ""));
  return clampFinite(marker?.timeSec, 0);
}

function bucketTimeSec(timeSec: number) {
  return Math.round(clampFinite(timeSec, 0) * 2) / 2;
}

function signalTimeSec(signal: TimelinePublishStripSignal): number | null {
  const payload = signal.payload ?? null;
  if (!payload || typeof payload !== "object") return null;
  const record = payload as Record<string, unknown>;
  if (typeof record.timeSec === "number" && Number.isFinite(record.timeSec)) return record.timeSec;
  if (typeof record.startSec === "number" && Number.isFinite(record.startSec)) return record.startSec;
  if (typeof record.playheadSec === "number" && Number.isFinite(record.playheadSec)) return record.playheadSec;
  return null;
}

function requiredLaneKinds(args: {
  importContext?: TimelinePublishStripImportContext | null;
  presentKinds: Set<TimelinePublishStripLaneKind>;
  hasRuntimeSignals: boolean;
}) {
  const required = new Set<TimelinePublishStripLaneKind>(args.presentKinds);

  switch (args.importContext?.importedKind) {
    case "audio":
      required.add("audio");
      required.add("intent");
      required.add("publish");
      break;
    case "npc":
      required.add("npc");
      required.add("intent");
      required.add("publish");
      break;
    case "image":
      required.add("creative");
      required.add("continuity");
      required.add("intent");
      required.add("publish");
      break;
    case "video":
      required.add("creative");
      required.add("timing");
      required.add("intent");
      required.add("publish");
      break;
    default:
      break;
  }

  if (args.hasRuntimeSignals) required.add("runtime");
  return required;
}

export function compileTimelinePublishStrip(input: TimelinePublishStripCompileInput): TimelinePublishStripContractV1 {
  const annotations = Array.isArray(input.annotations) ? input.annotations : [];
  const markers = Array.isArray(input.markers) ? input.markers : [];
  const signals = Array.isArray(input.signals) ? input.signals : [];

  const markerByAnnotationId = new Map<string, TimelineMarker>();
  for (const marker of markers) {
    const annotationId = String(marker?.annotation?.annotationId ?? "").trim();
    if (annotationId) markerByAnnotationId.set(annotationId, marker);
  }

  const grouped = new Map<string, { bucketTimeSec: number; annotations: TimelineAnnotation[]; signals: TimelinePublishStripSignal[] }>();

  for (const annotation of annotations) {
    const timeSec = bucketTimeSec(annotationTimeSec(annotation, markerByAnnotationId));
    const key = `t:${timeSec.toFixed(3)}`;
    const entry = grouped.get(key) ?? { bucketTimeSec: timeSec, annotations: [], signals: [] };
    entry.annotations.push(annotation);
    grouped.set(key, entry);
  }

  for (const signal of signals) {
    const timeSec = bucketTimeSec(signalTimeSec(signal) ?? 0);
    const key = `t:${timeSec.toFixed(3)}`;
    const entry = grouped.get(key) ?? { bucketTimeSec: timeSec, annotations: [], signals: [] };
    entry.signals.push(signal);
    grouped.set(key, entry);
  }

  const slices = Array.from(grouped.values())
    .sort((left, right) => left.bucketTimeSec - right.bucketTimeSec)
    .map((entry, index) => {
      const annotationsInSlice = entry.annotations.slice().sort((left, right) => {
        const timeDelta = annotationTimeSec(left, markerByAnnotationId) - annotationTimeSec(right, markerByAnnotationId);
        if (Math.abs(timeDelta) > 1e-9) return timeDelta;
        return Number(left.createdAt ?? 0) - Number(right.createdAt ?? 0);
      });

      const startSec = annotationsInSlice.length > 0
        ? Math.min(...annotationsInSlice.map((annotation) => annotationTimeSec(annotation, markerByAnnotationId)))
        : entry.bucketTimeSec;
      const endSec = annotationsInSlice.length > 0
        ? Math.max(...annotationsInSlice.map((annotation) => annotationTimeSec(annotation, markerByAnnotationId)))
        : entry.bucketTimeSec;

      const annotationsByKind = new Map<TimelineAnnotationKind, TimelineAnnotation[]>();
      for (const annotation of annotationsInSlice) {
        const list = annotationsByKind.get(annotation.type) ?? [];
        list.push(annotation);
        annotationsByKind.set(annotation.type, list);
      }

      const presentKinds = new Set<TimelinePublishStripLaneKind>(Array.from(annotationsByKind.keys()));
      if (entry.signals.length > 0) presentKinds.add("runtime");

      const requiredKinds = requiredLaneKinds({
        importContext: input.importContext,
        presentKinds,
        hasRuntimeSignals: entry.signals.length > 0,
      });

      const laneKinds = Array.from(new Set<TimelinePublishStripLaneKind>([...requiredKinds, ...presentKinds]));
      const lanes = laneKinds.map((laneKind) => {
        if (laneKind === "runtime") {
          return {
            lane: laneKind,
            state: entry.signals.length > 0 ? "present" : requiredKinds.has(laneKind) ? "missing" : "suggested",
            summary: entry.signals[0]?.label ?? null,
            nextAction: null,
            annotationIds: [],
            markerIds: [],
            signals: entry.signals,
          } satisfies TimelinePublishStripLaneEntry;
        }

        const laneAnnotations = annotationsByKind.get(laneKind) ?? [];
        const laneMarkers = laneAnnotations
          .map((annotation) => markerByAnnotationId.get(String(annotation.id ?? "")))
          .filter(Boolean) as TimelineMarker[];

        return {
          lane: laneKind,
          state: laneAnnotations.length > 0 ? "present" : requiredKinds.has(laneKind) ? "missing" : "suggested",
          summary: laneAnnotations[0]?.summary ?? laneAnnotations[0]?.rawNote ?? null,
          nextAction: laneAnnotations.find((annotation) => String(annotation.nextAction ?? "").trim())?.nextAction ?? null,
          annotationIds: laneAnnotations.map((annotation) => String(annotation.id ?? "")).filter(Boolean),
          markerIds: laneMarkers.map((marker) => String(marker.id ?? "")).filter(Boolean),
          signals: [],
        } satisfies TimelinePublishStripLaneEntry;
      });

      const missingLanes = lanes.filter((lane) => lane.state === "missing").map((lane) => lane.lane);
      const satisfiedLaneCount = lanes.filter((lane) => lane.state === "present").length;

      return {
        id: `slice-${index + 1}-${entry.bucketTimeSec.toFixed(3)}`,
        startSec,
        endSec,
        anchorMarkerId: lanes.flatMap((lane) => lane.markerIds).find(Boolean) ?? null,
        lanes,
        completeness: {
          requiredLaneCount: Array.from(requiredKinds).length,
          satisfiedLaneCount,
          missingLanes,
        },
      } satisfies TimelinePublishStripSlice;
    });

  return {
    v: TIMELINE_PUBLISH_STRIP_CONTRACT_VERSION,
    slices,
    sourceAnnotationCount: annotations.length,
    sourceMarkerCount: markers.length,
  };
}
